import styles from './deliveryDataList.module.css'
import { Operation } from '../../../../operations'
import { Delivery, DeliveryMethod, DeliveryOrder } from '../../../state/types'
import { Async, Badge, Button, DetailFieldProps, FieldList, FieldType, ModalId, Section, useUIActions } from '../../../../../features/ui'
import { AnnulDeliveryModal } from '../annul-delivery-modal/annulDeliveryModal'
import { DeliveryItemsStatsTable } from '../delivery-items-stats-table/deliveryItemsStatsTable'
import { addressHelpers } from '../../../../../features/address'
import { newId, useDataTypesHooks } from '../../../../../features/data-types'
import { useDeliveryOrderHooks } from '../../../hooks/deliveryOrderHooks'
import { deliveryHelpers, useDeliveryHooks } from '../../../hooks/deliveryHooks'
import { toDate } from '../../../../../utils/typeUtils'
import { fileHelpers } from '../../../../../features/files'
import { useEffect, useMemo, useState } from 'react'
import { CompanyUserName } from '../../../../system'

type Props = {
    deliveries: Delivery[]
    deliveryOrders?: DeliveryOrder[]
    operations: Operation[]
    defaultExpanded?: boolean
    onChangeCurrentDelivery?: (delivery: Delivery) => void
    onRefresh?: () => void
}

export const DeliveryDataList = (props: Props) => {
    const { deliveryOrders = [], operations, defaultExpanded, onChangeCurrentDelivery, onRefresh } = props
    
    const { describeDelivery, isAnnulable } = deliveryHelpers
    const { describeAddress } = addressHelpers
    const { deliveryNoteFileName2 } = fileHelpers
    const { formatDate, formatDateTime } = useDataTypesHooks()
    const { getDeliveryAddress } = useDeliveryOrderHooks()
    const { refreshDeliveries } = useDeliveryHooks()
    const { openModal } = useUIActions()

    const [deliveries, setDeliveries] = useState<Delivery[]>(props.deliveries)
    const [selectedDelivery, setSelectedDelivery] = useState<Delivery>()
    // A page can hold several lists (one per delivery order, plus the documents tab), all mounted even in hidden tabs:
    // with a shared id the first modal in the page opens, which can be hidden or belong to another list
    const annulModalId = useMemo(() => `${ModalId.ANNUL_DELIVERY}-${newId()}` as ModalId, [])

    useEffect(() => {
        setDeliveries(props.deliveries)
    }, [props.deliveries])

    const findDeliveryOrder = (delivery?: Delivery) => deliveryOrders.find(order => order.id === delivery?.orderId)
    const findOperation = (delivery: Delivery) => operations.find(operation => operation.id === delivery?.operationId)!

    const onDownloadDeliveryNote = (delivery: Delivery) => () => {
        onChangeCurrentDelivery && onChangeCurrentDelivery(delivery)
        openModal(ModalId.DOWNLOAD_DELIVERY_NOTE)
    }

    const onUploadDeliveryNote = (delivery: Delivery) => () => {
        onChangeCurrentDelivery && onChangeCurrentDelivery(delivery)
        openModal(ModalId.UPLOAD_DELIVERY_NOTE)
    }

    const onAnnulDelivery = (delivery: Delivery) => () => {
        setSelectedDelivery(delivery)
        openModal(annulModalId)
    }

    const title = (delivery: Delivery) => {
        return `${describeDelivery(delivery, findDeliveryOrder(delivery))} - ${formatDate(toDate(delivery.date))}`
    }

    const infoFields = (delivery: Delivery): DetailFieldProps[] => {
        const deliveryOrder = findDeliveryOrder(delivery)
        const isReception = delivery.method === DeliveryMethod.RECEPTION
        const fieldList = [
            {
                name: 'deliveryNote',
                type: FieldType.TEXT,
                label: '',
                render: () => (
                    <div className={styles.deliveryNoteButtons}>
                        {(!isReception || delivery.deliveryNoteUrl) && (
                            <Button
                                label={deliveryNoteFileName2(delivery, deliveryOrder)}
                                icon="download"
                                kind="tertiary"
                                small
                                onClick={onDownloadDeliveryNote(delivery)}
                            />
                        )}
                        {!deliveryOrder?.cancelled && !delivery.deliveryNoteUrl && (
                            <Button
                                label={isReception ? 'Subir Remito del Proveedor' : 'Subir Remito Firmado'}
                                icon="upload"
                                small
                                onClick={onUploadDeliveryNote(delivery)}
                            />
                        )}
                    </div>
                )
            },
            {
                name: 'date',
                type: FieldType.TEXT,
                label: delivery.method === DeliveryMethod.PICKUP ? 'Fecha de Retiro' : isReception ? 'Fecha de Recepción' : 'Fecha de Entrega',
                render: () => <>{formatDate(toDate(delivery.date))}</>
            },
            {
                name: 'deliveryAddress',
                type: FieldType.TEXT,
                label: 'Dirección de Entrega',
                render: () => <Async
                    loadFn={async () => {
                        const address = await getDeliveryAddress(delivery)!
                        return <>{describeAddress(address)}</>
                    }}
                />
            },
        ]
        if (isReception) {
            fieldList.splice(1, 0, {
                name: 'supplierDeliveryNoteNumber',
                type: FieldType.TEXT,
                label: 'Nro. Remito del Proveedor',
                render: () => <>{delivery.supplierDeliveryNoteNumber || '-'}</>
            })
        }
        if (delivery.cancelled) {
            fieldList.push({
                name: 'cancelled',
                type: FieldType.TEXT,
                label: 'Estado',
                render: () => <Badge label='Cancelado' />
            })
            if (delivery.negativeStateUserId) {
                fieldList.push({
                    name: 'negativeStateUserId',
                    type: FieldType.TEXT,
                    label: 'Cancelado por',
                    render: () => {
                        const dateStr = delivery.negativeStateAt ? ` - ${formatDateTime(new Date(delivery.negativeStateAt))}` : ''
                        return <><CompanyUserName userId={delivery.negativeStateUserId!} />{dateStr}</>
                    }
                })
            }
        }
        return fieldList
    }

    const onAnnul = async () => {
        const refreshedDeliveries = await refreshDeliveries(deliveries)
        setDeliveries(refreshedDeliveries)
        onRefresh && onRefresh()
    }

    // Most recently emitted first
    const sortedDeliveries = [...deliveries].sort((a, b) => new Date(b.creationDate).getTime() - new Date(a.creationDate).getTime() || b.code - a.code)
    const activeDeliveries = sortedDeliveries.filter(delivery => !delivery.cancelled)
    // Cancelled delivery notes are grouped apart and collapsed, so they aren't mistaken for valid ones
    const cancelledDeliveries = sortedDeliveries.filter(delivery => delivery.cancelled)

    const renderDelivery = (delivery: Delivery) => (
        <Section
            key={delivery.id}
            id={`delivery-${delivery.id}`}
            title={title(delivery)}
            collapsable
            defaultExpanded={defaultExpanded}
            space={12}
        >
            <FieldList fields={infoFields(delivery)} />
            {!delivery.cancelled ? (
                <DeliveryItemsStatsTable
                    delivery={delivery}
                    operation={findOperation(delivery)}
                />
            ) : <></>}
            {isAnnulable(delivery, findDeliveryOrder(delivery)) ? (
                <div className={styles.actions}>
                    <Button
                        label="Anular Entrega"
                        icon="slash-circle"
                        kind="secondary"
                        small
                        onClick={onAnnulDelivery(delivery)}
                    />
                </div>
            ) : <></>}
        </Section>
    )

    return (
        <div className={styles.container}>
            {activeDeliveries.map(renderDelivery)}
            {cancelledDeliveries.length > 0 && (
                <Section
                    id={`cancelled-deliveries-${cancelledDeliveries[0].id}`}
                    title={`Remitos Cancelados (${cancelledDeliveries.length})`}
                    collapsable
                    space={12}
                >
                    <div className={styles.container}>
                        {cancelledDeliveries.map(renderDelivery)}
                    </div>
                </Section>
            )}
            <AnnulDeliveryModal
                delivery={selectedDelivery}
                deliveryOrder={findDeliveryOrder(selectedDelivery)}
                operation={selectedDelivery && findOperation(selectedDelivery)}
                modalId={annulModalId}
                onAnnul={onAnnul}
            />
        </div>
    )
}
